/**
 * Analytics Service
 *
 * Centralized analytics tracking for:
 * - Google Analytics 4 (GA4)
 * - Contentsquare/Hotjar
 * - Custom database analytics (Phase 2)
 *
 * Handles page views, events, conversions, and user identification
 * Stores data both in GA4 and local database for custom reporting
 */

import ReactGA from "react-ga4";

import { supabase } from "@/lib/supabase";

class AnalyticsService {
  private initialized = false;
  private gaEnabled = false;
  private sessionId: string | null = null;

  /**
   * Initialize analytics services
   * Call this after user consents to tracking
   */
  initialize() {
    if (this.initialized) {
      console.log("📊 Analytics already initialized");
      return;
    }

    const gaId = import.meta.env.VITE_GA_MEASUREMENT_ID;
    const analyticsEnabled = import.meta.env.VITE_ENABLE_ANALYTICS === "true";

    if (!analyticsEnabled) {
      console.log("📊 Analytics disabled via VITE_ENABLE_ANALYTICS");
      return;
    }

    // Initialize Google Analytics 4
    if (gaId) {
      try {
        ReactGA.initialize(gaId, {
          gaOptions: {
            anonymizeIp: true, // GDPR compliance
            cookieFlags: "SameSite=None;Secure",
          },
          gtagOptions: {
            send_page_view: false, // We'll send manually in pageView()
          },
        });
        this.gaEnabled = true;
        console.log("✅ Google Analytics initialized:", gaId);
      } catch (error) {
        console.error("❌ GA initialization failed:", error);
      }
    } else {
      console.warn("⚠️ VITE_GA_MEASUREMENT_ID not found");
    }

    this.initialized = true;
    this.sessionId = this.getSessionId();

    // Initialize session in database
    this.initSession().catch(console.error);

    console.log("📊 Analytics service initialized");
  }

  /**
   * Track page view
   * @param path - Page path (e.g., '/about')
   * @param title - Page title (optional, defaults to document.title)
   */
  pageView(path: string, title?: string) {
    // Track in GA4
    if (this.gaEnabled) {
      try {
        ReactGA.send({
          hitType: "pageview",
          page: path,
          title: title || document.title,
        });

        if (import.meta.env.VITE_ENABLE_DEBUG === "true") {
          console.log("📄 Page view tracked:", {
            path,
            title: title || document.title,
          });
        }
      } catch (error) {
        console.error("❌ Page view tracking failed:", error);
      }
    }

    // Store in database (Phase 2)
    this.storeEvent("page_view", "navigation", "viewed", path, undefined, {
      title: title || document.title,
    }).catch(console.error);
  }

  /**
   * Track custom event
   * @param category - Event category (e.g., 'User', 'Tool', 'Navigation')
   * @param action - Event action (e.g., 'Login', 'Calculate ROI', 'Click CTA')
   * @param label - Optional event label
   * @param value - Optional numeric value
   */
  event(category: string, action: string, label?: string, value?: number) {
    if (!this.gaEnabled) return;

    try {
      ReactGA.event({
        category,
        action,
        label,
        value,
      });

      if (import.meta.env.VITE_ENABLE_DEBUG === "true") {
        console.log("📊 Event tracked:", { category, action, label, value });
      }
    } catch (error) {
      console.error("❌ Event tracking failed:", error);
    }
  }

  /**
   * Track conversion (important business event)
   * @param type - Conversion type (e.g., 'calendar_booking', 'tool_completed')
   * @param value - Optional conversion value
   * @param metadata - Additional metadata about the conversion
   */
  conversion(type: string, value?: number, metadata?: Record<string, any>) {
    // Track in GA4
    if (this.gaEnabled) {
      try {
        ReactGA.event({
          category: "Conversion",
          action: type,
          label: metadata?.source || "unknown",
          value: value || 1,
        });

        if (import.meta.env.VITE_ENABLE_DEBUG === "true") {
          console.log("💰 Conversion tracked:", { type, value, metadata });
        }
      } catch (error) {
        console.error("❌ Conversion tracking failed:", error);
      }
    }

    // Store in database (Phase 2)
    this.storeConversion(
      type,
      metadata?.source || "unknown",
      value,
      metadata,
    ).catch(console.error);
  }

  /**
   * Identify user after authentication
   * @param userId - Unique user identifier
   * @param properties - Additional user properties
   */
  identifyUser(userId: string, properties?: Record<string, any>) {
    // Set user ID in GA4
    if (this.gaEnabled) {
      try {
        ReactGA.set({ userId });

        // Set additional user properties if provided
        if (properties) {
          ReactGA.set(properties);
        }

        if (import.meta.env.VITE_ENABLE_DEBUG === "true") {
          console.log("👤 User identified:", { userId, properties });
        }
      } catch (error) {
        console.error("❌ User identification failed:", error);
      }
    }
  }

  /**
   * Track tool usage with specific parameters
   * @param toolName - Name of the tool (e.g., 'ROI Calculator')
   * @param action - Action taken (e.g., 'Calculation Complete')
   * @param parameters - Tool-specific parameters
   */
  trackToolUsage(
    toolName: string,
    action: string,
    parameters?: Record<string, any>,
  ) {
    // Track in GA4
    this.event(
      "Tool",
      `${toolName}: ${action}`,
      JSON.stringify(parameters),
      parameters?.value,
    );

    // Store in database (Phase 2)
    this.storeToolUsage(
      toolName,
      action,
      parameters,
      parameters?.results || {},
    ).catch(console.error);
  }

  /**
   * Track outbound link click
   * @param url - Destination URL
   * @param label - Optional label describing the link
   */
  trackOutboundLink(url: string, label?: string) {
    this.event("Outbound Link", "Click", label || url);
  }

  /**
   * Track form submission
   * @param formName - Name of the form
   * @param success - Whether submission was successful
   */
  trackFormSubmission(formName: string, success: boolean) {
    this.event("Form", success ? "Submit Success" : "Submit Failed", formName);
  }

  /**
   * Check if user has granted analytics consent
   */
  hasConsent(): boolean {
    return localStorage.getItem("analytics_consent") === "true";
  }

  /**
   * Set analytics consent
   * @param granted - Whether consent is granted
   */
  setConsent(granted: boolean) {
    localStorage.setItem("analytics_consent", granted ? "true" : "false");

    if (granted) {
      this.initialize();

      // Track consent granted
      this.event("Privacy", "Consent Granted", "Analytics");
    } else {
      console.log("📊 Analytics consent declined");
    }
  }

  /**
   * Get or create session ID
   * Session ID persists for the browser session (cleared on browser close)
   */
  private getSessionId(): string {
    let sessionId = sessionStorage.getItem("analytics_session_id");

    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem("analytics_session_id", sessionId);
    }

    return sessionId;
  }

  /**
   * Get current session ID
   */
  getSession(): string {
    return this.sessionId || this.getSessionId();
  }

  /**
   * Track timing (for performance monitoring)
   * @param category - Timing category
   * @param variable - Timing variable
   * @param value - Time in milliseconds
   * @param label - Optional label
   */
  trackTiming(
    category: string,
    variable: string,
    value: number,
    label?: string,
  ) {
    if (!this.gaEnabled) return;

    try {
      ReactGA.event({
        category: "Timing",
        action: category,
        label: `${variable}${label ? `: ${label}` : ""}`,
        value: Math.round(value),
      });

      if (import.meta.env.VITE_ENABLE_DEBUG === "true") {
        console.log("⏱️ Timing tracked:", { category, variable, value, label });
      }
    } catch (error) {
      console.error("❌ Timing tracking failed:", error);
    }
  }

  /**
   * Track exception/error
   * @param description - Error description
   * @param fatal - Whether the error was fatal
   */
  trackException(description: string, fatal = false) {
    if (!this.gaEnabled) return;

    try {
      ReactGA.event({
        category: "Exception",
        action: fatal ? "Fatal Error" : "Non-Fatal Error",
        label: description,
      });

      console.error("🚨 Exception tracked:", { description, fatal });
    } catch (error) {
      console.error("❌ Exception tracking failed:", error);
    }
  }

  // ============================================================================
  // DATABASE ANALYTICS (PHASE 2)
  // ============================================================================

  /**
   * Store event in database
   * @private
   */
  private async storeEvent(
    eventType: string,
    eventCategory: string,
    eventAction: string,
    eventLabel?: string,
    eventValue?: number,
    metadata?: Record<string, any>,
  ) {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      await supabase.from("analytics_events").insert({
        session_id: this.getSession(),
        user_id: user?.id || null,
        event_type: eventType,
        event_category: eventCategory,
        event_action: eventAction,
        event_label: eventLabel || null,
        event_value: eventValue || null,
        page_path: window.location.pathname,
        page_title: document.title,
        referrer: document.referrer || null,
        metadata: metadata || {},
      });

      if (import.meta.env.VITE_ENABLE_DEBUG === "true") {
        console.log("💾 Event stored in database:", {
          eventType,
          eventCategory,
          eventAction,
        });
      }
    } catch (error) {
      console.error("❌ Database event storage failed:", error);
    }
  }

  /**
   * Store conversion in database
   * @private
   */
  private async storeConversion(
    conversionType: string,
    source: string,
    value?: number,
    metadata?: Record<string, any>,
  ) {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      // First store the event
      const { data: eventData } = await supabase
        .from("analytics_events")
        .insert({
          session_id: this.getSession(),
          user_id: user?.id || null,
          event_type: "conversion",
          event_category: conversionType,
          event_action: "completed",
          page_path: window.location.pathname,
          page_title: document.title,
          referrer: document.referrer || null,
          metadata: metadata || {},
        })
        .select()
        .single();

      if (eventData) {
        // Then store the conversion
        await supabase.from("analytics_conversions").insert({
          event_id: eventData.id,
          session_id: this.getSession(),
          user_id: user?.id || null,
          conversion_type: conversionType,
          source: source,
          conversion_value: value || null,
          metadata: metadata || {},
        });

        if (import.meta.env.VITE_ENABLE_DEBUG === "true") {
          console.log("💾 Conversion stored in database:", {
            conversionType,
            source,
          });
        }
      }
    } catch (error) {
      console.error("❌ Database conversion storage failed:", error);
    }
  }

  /**
   * Store tool usage in database
   * @private
   */
  private async storeToolUsage(
    toolName: string,
    action: string,
    parameters?: Record<string, any>,
    results?: Record<string, any>,
  ) {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      // First store the event
      const { data: eventData } = await supabase
        .from("analytics_events")
        .insert({
          session_id: this.getSession(),
          user_id: user?.id || null,
          event_type: "tool_usage",
          event_category: toolName,
          event_action: action,
          page_path: window.location.pathname,
          page_title: document.title,
          referrer: document.referrer || null,
          metadata: { parameters, results },
        })
        .select()
        .single();

      if (eventData) {
        // Then store the tool usage
        await supabase.from("analytics_tool_usage").insert({
          event_id: eventData.id,
          session_id: this.getSession(),
          user_id: user?.id || null,
          tool_name: toolName,
          tool_action: action,
          parameters: parameters || {},
          results: results || {},
        });

        if (import.meta.env.VITE_ENABLE_DEBUG === "true") {
          console.log("💾 Tool usage stored in database:", {
            toolName,
            action,
          });
        }
      }
    } catch (error) {
      console.error("❌ Database tool usage storage failed:", error);
    }
  }

  /**
   * Initialize or update session in database
   * @private
   */
  private async initSession() {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const sessionId = this.getSession();

      // Check if session exists
      const { data: existingSession } = await supabase
        .from("analytics_sessions")
        .select("id")
        .eq("session_id", sessionId)
        .single();

      if (!existingSession) {
        // Create new session
        await supabase.from("analytics_sessions").insert({
          session_id: sessionId,
          user_id: user?.id || null,
          landing_page: window.location.pathname,
          referrer: document.referrer || null,
        });

        if (import.meta.env.VITE_ENABLE_DEBUG === "true") {
          console.log("💾 Session initialized in database:", sessionId);
        }
      }
    } catch (error) {
      console.error("❌ Database session initialization failed:", error);
    }
  }
}

// Export singleton instance
export const analytics = new AnalyticsService();
